import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Info } from 'lucide-react'

export interface ParseConfig {
  chunkMethod: string
  chunkTokenNum: number
  delimiter: string
  layoutRecognize: string
}

interface Step2ParseConfigProps {
  config: ParseConfig
  onConfigChange: (config: ParseConfig) => void
}

const CHUNK_METHODS = [
  { value: 'naive', label: 'General', description: 'Best for most document types. Splits text into fixed-size chunks.' },
  { value: 'book', label: 'Book', description: 'Optimized for long-form book content with chapter awareness.' },
  { value: 'manual', label: 'Manual', description: 'For technical manuals with structured sections and headings.' },
  { value: 'qa', label: 'Q&A', description: 'Extracts question-answer pairs from FAQ-style documents.' },
  { value: 'table', label: 'Table', description: 'Specialized for tabular data in spreadsheets or documents.' },
  { value: 'paper', label: 'Paper', description: 'Handles academic papers with abstracts, sections, and references.' },
  { value: 'laws', label: 'Laws', description: 'Parses legal documents preserving article and clause structure.' },
  { value: 'presentation', label: 'Presentation', description: 'Processes slide decks, extracting content per slide.' },
  { value: 'picture', label: 'Picture', description: 'Extracts text from images using OCR.' },
  { value: 'one', label: 'One', description: 'Treats the entire document as a single chunk. Good for short docs.' },
  { value: 'email', label: 'Email', description: 'Parses email threads preserving sender, date, and thread structure.' },
]

const LAYOUT_OPTIONS = [
  { value: 'DeepDOC', label: 'DeepDOC (Recommended)', description: 'AI-powered layout analysis for PDFs with complex formatting.' },
  { value: 'PlainText', label: 'Plain Text', description: 'Simple text extraction without layout analysis. Faster but less accurate for PDFs.' },
]

const DEFAULT_PARSE_CONFIG: ParseConfig = {
  chunkMethod: 'naive',
  chunkTokenNum: 512,
  delimiter: '\\n',
  layoutRecognize: 'DeepDOC',
}

// Chunk methods that support chunk_token_num and delimiter
const NAIVE_LIKE_METHODS = ['naive']

export function Step2ParseConfig({ config, onConfigChange }: Step2ParseConfigProps) {
  const showChunkSettings = NAIVE_LIKE_METHODS.includes(config.chunkMethod)
  const selectedMethod = CHUNK_METHODS.find((m) => m.value === config.chunkMethod)

  const handleReset = () => {
    onConfigChange(DEFAULT_PARSE_CONFIG)
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-1">Parse Configuration</h3>
        <p className="text-sm text-muted-foreground">
          These settings control how your documents are split into chunks for indexing. The right chunking strategy
          significantly impacts retrieval quality.
        </p>
      </div>

      {/* Chunk Method */}
      <div className="space-y-2">
        <Label>Chunking Method</Label>
        <Select
          value={config.chunkMethod}
          onValueChange={(value) => onConfigChange({ ...config, chunkMethod: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select chunking method" />
          </SelectTrigger>
          <SelectContent>
            {CHUNK_METHODS.map((method) => (
              <SelectItem key={method.value} value={method.value}>
                {method.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedMethod && (
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            {selectedMethod.description}
          </p>
        )}
      </div>

      {/* Embedding Model (Info only) */}
      <div className="space-y-2">
        <Label>Embedding Model</Label>
        <Input
          value="text-embedding-3-large (OpenAI)"
          disabled
          className="bg-muted"
        />
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          The embedding model converts text into vectors for semantic search. This is managed at the organization level
          and cannot be changed per knowledge base.
        </p>
      </div>

      {/* Conditional: Chunk Token Size + Delimiter (only for "naive") */}
      {showChunkSettings && (
        <>
          {/* Chunk Token Size */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Chunk Size (tokens)</Label>
              <span className="text-sm font-medium">{config.chunkTokenNum}</span>
            </div>
            <Slider
              value={[config.chunkTokenNum]}
              onValueChange={([value]) => onConfigChange({ ...config, chunkTokenNum: value })}
              min={64}
              max={2048}
              step={64}
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Controls the size of each text chunk. Smaller chunks (128–256) are more precise but may lose context.
              Larger chunks (512–1024) preserve more context but may include irrelevant information. Start with 512
              and adjust based on retrieval quality.
            </p>
          </div>

          {/* Delimiter */}
          <div className="space-y-2">
            <Label htmlFor="delimiter">Delimiter</Label>
            <Input
              id="delimiter"
              value={config.delimiter}
              onChange={(e) => onConfigChange({ ...config, delimiter: e.target.value })}
              placeholder="\\n"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              The character(s) used to split text before chunking. Common delimiters: <code className="bg-muted px-1 rounded">\n</code> (newline),
              {' '}<code className="bg-muted px-1 rounded">\n\n</code> (paragraph), or custom separators like <code className="bg-muted px-1 rounded">---</code>.
            </p>
          </div>
        </>
      )}

      {/* Layout Recognition */}
      <div className="space-y-2">
        <Label>Layout Recognition</Label>
        <Select
          value={config.layoutRecognize}
          onValueChange={(value) => onConfigChange({ ...config, layoutRecognize: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select layout recognition" />
          </SelectTrigger>
          <SelectContent>
            {LAYOUT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          {LAYOUT_OPTIONS.find((o) => o.value === config.layoutRecognize)?.description ||
            'Select a layout recognition mode for PDF processing.'}
        </p>
      </div>

      {/* Reset Button */}
      <div className="pt-2">
        <Button type="button" variant="outline" onClick={handleReset}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  )
}

export { DEFAULT_PARSE_CONFIG }
